import { useState } from "react"
import { Link } from "react-router-dom"
import { Navigate, useNavigate } from "react-router-dom"
import { socket } from "../utils/utils"
import { useAppSelector } from "../store/hooks"
import AuthLayout, {
  authGhostButton,
  authInput,
  authMutedText,
  authPrimaryButton,
  authSubtleLink,
} from "../Components/AuthLayout"
import wallpaper from "@/images/backgrounds/wallpaper-forgot-pass.webp"

interface ForgotPasswordProps {}

const ForgotPasswordOtp: React.FC<ForgotPasswordProps> = () => {
  const [otp, setOtp] = useState("")
  const nav = useNavigate()
  const forgetPasswordEmail = useAppSelector(
    (state) => state.global.forgetPasswordEmail
  )

  const verifyOtp = () => {
    socket.emit(
      "verifyOtpForForgetPass",
      { email: forgetPasswordEmail, otp },
      (res) => {
        if (res) {
          nav("/set-new-password")
          return
        }
      }
    )
  }

  const resendOtp = () => {
    socket.emit("requestResetPass", forgetPasswordEmail)
  }

  // The address lives in the store, not the URL, so a reload or a deep link
  // lands here with nothing to verify against. Send them back a step rather
  // than let them type a code that can only ever fail.
  if (!forgetPasswordEmail) {
    return <Navigate to="/forgot-pass" replace></Navigate>
  }

  return (
    <AuthLayout
      wallpaper={wallpaper}
      marketingTitle="Check your inbox"
      marketingBody={
        <>
          <div>The code is good for a few minutes and one attempt at a time.</div>
          <div>Nothing there? Look in spam before asking for another.</div>
        </>
      }
      marketingCta={{ to: "/login", label: "Back to Login" }}
      heading="Enter your code"
      subheading={
        <>
          We sent a one-time code to{" "}
          <span className="font-semibold text-brown-700 mobile:text-amber-300">
            {forgetPasswordEmail}
          </span>
          .
        </>
      }
      footer={
        <div className={`mt-8 text-center sm:mt-4 ${authMutedText}`}>
          <Link to="/login" className={authSubtleLink}>
            Back to login
          </Link>
        </div>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          verifyOtp()
        }}
        className="flex flex-col gap-4 sm:gap-3"
      >
        <input
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          data-testid="otp-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="Enter OTP"
          className={`${authInput} text-center tracking-[0.4em]`}
        />
        <button
          type="submit"
          data-testid="otp-btn"
          className={authPrimaryButton}
        >
          Verify code
        </button>

        <div className={`mt-2 text-center ${authMutedText}`}>
          Haven't received the email?
        </div>
        <button
          type="button"
          onClick={resendOtp}
          data-testid="resend-otp-btn"
          className={authGhostButton}
        >
          Resend code
        </button>
      </form>
    </AuthLayout>
  )
}

export default ForgotPasswordOtp
